// format contains help for the parser to decide correct date
// possible values:
// YMD, DMY, MDY
export function parseDate (dateStr: string, format: string): Date | null {
  if (!dateStr) return null;

  let parsedDate = null;

  // Prøver ISO format
  if (dateStr.includes('T')) {
    const [datePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-');
    return new Date(Date.UTC(+year, +month - 1, +day));
  }

  if (format === 'YMD') {
    parsedDate = parseDateYmd(dateStr);
  } else if (format === 'MDY') {
    parsedDate = parseDateMdy(dateStr);
  } else if (format === 'DMY') {
    parsedDate = parseDateDmy(dateStr);
  }

  // if successfully parsed with given format return it.
  if (parsedDate !== null)
    return parsedDate;

  // otherwise fallback to more general formats.

  // Håndterer DD.MM.YYYY format
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('.');
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // Håndterer DDMMYYYY format
  if (/^\d{8}$/.test(dateStr)) {
    const day = dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const year = dateStr.substring(4, 8);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // Håndterer DD.MM.YYYY HH:mm[:ss[.SSS]] format
  if (/^\d{2}\.\d{2}\.\d{4}\s\d{2}:\d{2}(:\d{2})?(\.\d{1,3})?$/.test(dateStr)) {
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('.');
    const timeComponents = timePart.split(/[:.]/);

    const date = new Date(Date.UTC(
      +year,
      +month - 1,
      +day,
      +timeComponents[0],
      +timeComponents[1],
      timeComponents[2] ? +timeComponents[2] : 0,
      timeComponents[3] ? +timeComponents[3] : 0,
    ));
    return isValidDate(date) ? date : null;
  }

  // Prøver ISO format
  const date = new Date(dateStr);
  return isValidDate(date) ? date : null;
}


function parseDateDmy (dateStr: string): Date | null {
  const separator = findSeparator(dateStr);

  // DD.MM.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}${separator}\\d{4}$`)).test(dateStr)) {
    const [day, month, year] = dateStr.split(separator);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // DD.MM.YY is expanded to DD.MM.20YY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}${separator}\\d{2}$`)).test(dateStr)) {
    const [day, month, year] = dateStr.split(separator);
    const date = new Date(Date.UTC(Number('20' + year), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // DD.MM is expanded to DD.MM.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}$`)).test(dateStr)) {
    const [day, month] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // DD. is expanded to DD.MM.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}$`)).test(dateStr)) {
    const [day] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), +day));
    return isValidDate(date) ? date : null;
  }

  // D. is expanded to DD.MM.YYYY
  if (new RegExp(safe(`^\\d{1}${separator}$`)).test(dateStr)) {
    const [day] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), +day));
    return isValidDate(date) ? date : null;
  }

  // D.M is expanded to DD.MM.YYYY
  if (new RegExp(safe(`^\\d{1}${separator}\\d{1}$`)).test(dateStr)) {
    const [day, month] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // DD.M is expanded to DD.MM.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{1}$`)).test(dateStr)) {
    const [day, month] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // D.MM is expanded to DD.MM.YYYY
  if (new RegExp(safe(`^\\d{1}${separator}\\d{2}$`)).test(dateStr)) {
    const [day, month] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }
  return null;
}

function parseDateMdy (dateStr: string): Date | null {
  const separator = findSeparator(dateStr);

  // MM.DD expanded to MM.DD.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}$`)).test(dateStr)) {
    const [month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // MM.DD.YY expanded to MM.DD.20YY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}${separator}\\d{2}$`)).test(dateStr)) {
    const [month, day, year] = dateStr.split(separator);
    const date = new Date(Date.UTC(Number('20' + year), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // MM.DD.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}${separator}\\d{4}$`)).test(dateStr)) {
    const [month, day, year] = dateStr.split(separator);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }
  return null;
}

function findSeparator (dateStr: string) {
  let separator = '.';
  if (dateStr.indexOf('/') !== -1) {
    separator = '/';
  } else if (dateStr.indexOf('-') !== -1) {
    separator = '-';
  }
  return separator;
}

function parseDateYmd (dateStr: string): Date | null {
  const separator = findSeparator(dateStr);

  // YYYY.MM.DD
  let regex = safe(`^\\d{4}${separator}\\d{2}${separator}\\d{2}$`);
  if (new RegExp(regex).test(dateStr)) {
    const [year, month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // YY.MM.DD expanded to 20YY.MM.DD
  regex = safe(`^\\d{2}${separator}\\d{2}${separator}\\d{2}$`);
  if (new RegExp(regex).test(dateStr)) {
    const [year, month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(Number('20' + year), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // MM.DD expanded to YYYY.MM.DD
  regex = safe(`^\\d{2}${separator}\\d{2}$`);
  if (new RegExp(regex).test(dateStr)) {
    const [month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  return null;
}

// apply escaping of reserved characters '.' and '/' in string that will be used in regex
function safe (regex: string) {
  return regex.replace(/[.\/]/g, '\\$&');
}


export function formatDateOnly (dateValue: string | Date | null): string {
  if (!dateValue) return '';

  // Hvis det er en string på ISO format (YYYY-MM-DDTHH:mm:ss)
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    const [datePart] = dateValue.split('T');
    const [year, month, day] = datePart.split('-');
    return `${day}.${month}.${year}`;
  }

  const date = dateValue instanceof Date ? dateValue : getUTCDate(dateValue);
  if (!isValidDate(date)) return String(dateValue);

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}.${month}.${year}`;
}

export function formatDate (dateValue: string | Date | null): string {
  if (!dateValue) return '';

  // Hvis det er en string, parse den direkte for å unngå tidssone-konvertering
  const date = dateValue instanceof Date ? dateValue : getUTCDate(dateValue);
  if (!isValidDate(date)) {
    console.warn('Invalid date', dateValue);
    return String(dateValue);
  }

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  const milliseconds = date.getUTCMilliseconds().toString().padStart(3, '0');

  let result = `${day}.${month}.${year}`;

  const hasTime = date.getUTCHours() || date.getUTCMinutes();
  if (hasTime) {
    result += ` ${hours}:${minutes}`;
  }

  if (seconds !== '00' || milliseconds !== '000') {
    result += `:${seconds}`;
  }

  if (milliseconds !== '000') {
    result += `.${milliseconds}`;
  }

  return result;
}

export function getUTCDate (dateValue: string): Date {
  if (dateValue.includes('T')) {
    const [datePart, timePart] = dateValue.replace('Z', '').split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second = 0] = timePart.split(':').map(Number);
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  }
  throw new Error('Invalid date value');
}

export function isValidDate (date: Date): boolean {
  return !isNaN(date.getTime());
}
